import classnames from 'classnames';
import styles from './Dial.module.scss';

const CENTER = 50;
const TICK_INNER_RADIUS = 35;
const TICK_OUTER_RADIUS = 49;

interface DialProps {
    /** Filled amount, from 0 to 100, or null when this dial has no range defined yet. */
    value: number | null;
    label: string;
    /** Percentages (0-100) to mark around the ring. A mark is drawn as a notch that changes once `value` reaches it. */
    milestones?: readonly number[];
    /** Extra text for the tooltip and accessible name, e.g. what the current value has earned. */
    detail?: string;
    className?: string;
}

/** Returns the point at `percent` of a full turn, clockwise from 12 o'clock, at `radius` from the dial's center. */
function pointOnDial(percent: number, radius: number): { x: number; y: number } {
    const angle = (percent / 100) * 2 * Math.PI - Math.PI / 2;

    return { x: CENTER + radius * Math.cos(angle), y: CENTER + radius * Math.sin(angle) };
}

/** A circular gauge: a ring that fills clockwise, with the value in the middle and a label beneath. */
export function Dial({ value, label, milestones = [], detail, className }: DialProps): JSX.Element {
    const isMeasured = value !== null;
    const description = !isMeasured
        ? `${label}: not tracked yet`
        : detail ? `${label}: ${value}% (${detail})` : `${label}: ${value}%`;

    return (
        <div
            className={classnames(styles.dial, !isMeasured && styles.unmeasured, className)}
            role="progressbar"
            aria-label={label}
            aria-valuetext={description}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={value ?? undefined}
            title={description}
        >
            <div className={styles.face}>
                <svg className={styles.ring} viewBox="0 0 100 100" aria-hidden="true">
                    <circle className={styles.track} cx={CENTER} cy={CENTER} r="42" />
                    {isMeasured && (
                        <circle
                            className={styles.fill}
                            cx={CENTER}
                            cy={CENTER}
                            r="42"
                            pathLength="100"
                            style={{ strokeDasharray: `${value} 100` }}
                        />
                    )}
                    {isMeasured && milestones.map((milestone) => {
                        const from = pointOnDial(milestone, TICK_INNER_RADIUS);
                        const to = pointOnDial(milestone, TICK_OUTER_RADIUS);

                        return (
                            <line
                                key={milestone}
                                className={classnames(styles.milestone, value >= milestone && styles.reached)}
                                x1={from.x}
                                y1={from.y}
                                x2={to.x}
                                y2={to.y}
                            />
                        );
                    })}
                </svg>

                <span className={styles.value}>{isMeasured ? `${value}%` : '-'}</span>
            </div>

            <span className={styles.label}>{label}</span>
        </div>
    );
}
